import { html, css } from 'lit-element';
import { BaseElement } from './BaseElement';
import { API, EVENT_TYPES } from '../API';
import { DoctorKneeProtocol } from '../DoctorKneeProtocol';
import { DIALOG_TYPE_ERROR } from './ModalDialog';
import * as GUID from '../GUID';

import './SurveyQuestion';

const _doctorKneeProtocol = Symbol();
const _api = Symbol();
const _statusHandler = Symbol();

class SurveyQuestions extends BaseElement {
	constructor() {
		super();

		this[_statusHandler] = () => {
			if(this.api.loggedIn && !this.doctorKneeProtocol && this.api.isPhysician)
				this.getDoctorKneeProtocol();
			else if(!this.api.loggedIn)
				this.questions = null;
			this.requestUpdate();
		};
	}
	static get styles() {
		return [
			...BaseElement.styles,
			css`
				:host {
					display: block;
				}
				legend {
					padding: 0 0.5em;
					border-bottom: none;
					font-weight: bold;
				}
				.options {
					display: flex;
					flex-flow: row wrap;
				}
				.options > * {
					flex: 1 0;
					white-space: nowrap;
					border: var(--theme-border, black);
					border-radius: 0.25em;
					background-color: var(--theme-background-hover, white);
					padding: 0.25em;
					margin: 0.25em;
					display: flex;
					flex-flow: column nowrap;
				}
			`
		];
	}
	get api() {
		return this[_api];
	}
	set api(v) {
		if(v !== null && !(v instanceof API))
			throw new TypeError('api must be an instance of API.');
		this[_api] = v;
	}
	get doctorKneeProtocol() {
		return this[_doctorKneeProtocol];
	}
	set doctorKneeProtocol(v) {
		if(v !== null && !(v instanceof DoctorKneeProtocol))
			throw new TypeError('types must be an instance of DoctorKneeProtocol.');
		this[_doctorKneeProtocol] = v;
		this.requestUpdate();
	}
	async getDoctorKneeProtocol() {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			if(!this.api)
				throw new Error('no api set.');
			this.doctorKneeProtocol = await this.api.doctorKneeProtocol();
			this.emit('hide-busy', id);
		}
		catch(err) {
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Doctor Knee Protocol',
				message: err.message,
				type: DIALOG_TYPE_ERROR
			});
		}
	}
	async handleTargetFlexion(e) {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			const targetFlexion = parseInt(e.target.value, 10);
			if(!this.api)
				throw new Error('no api set.');
			await this.api.doctorKneeProtocolTargetFlexion(targetFlexion);
			this.doctorKneeProtocol.targetFlexion = targetFlexion;
			this.emit('hide-busy', id);
		}
		catch(err) {
			this.requestUpdate();
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Target Flexion',
				message: err.message,
				type: DIALOG_TYPE_ERROR
			});
		}
		
	}
	async handleTargetExtension(e) {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			const targetExtension = parseInt(e.target.value, 10);
			if(!this.api)
				throw new Error('no api set.');
			await this.api.doctorKneeProtocolTargetExtension(targetExtension);
			this.doctorKneeProtocol.targetExtension = targetExtension;
			this.emit('hide-busy', id);
		}
		catch(err) {
			this.requestUpdate();
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Target Extension',
				message: err.message,
				type: DIALOG_TYPE_ERROR
			});
		}
		
	}
	async handleSessionsPerDay(e) {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			const sessionsPerDay = parseInt(e.target.value, 10);
			if(!this.api)
				throw new Error('no api set.');
			await this.api.doctorKneeProtocolSessionsPerDay(sessionsPerDay);
			this.doctorKneeProtocol.sessionsPerDay = sessionsPerDay;
			this.emit('hide-busy', id);
		}
		catch(err) {
			this.requestUpdate();
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Sessions Per Day',
				message: err.message,
				type: DIALOG_TYPE_ERROR
			});
		}
		
	}
	async handleSessionDuration(e) {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			const sessionDuration = parseInt(e.target.value, 10);
			if(!this.api)
				throw new Error('no api set.');
			await this.api.doctorKneeProtocolSessionDuration(sessionDuration);
			this.doctorKneeProtocol.sessionDuration = sessionDuration;
			this.emit('hide-busy', id);
		}
		catch(err) {
			this.requestUpdate();
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Session Duration',
				message: err.message,
				type: DIALOG_TYPE_ERROR
			});
		}
		
	}
	connectedCallback() {
		super.connectedCallback();

		this.emit('get-api', { callback: api => this.api = api });
		this.api.on(EVENT_TYPES.EVENT_TYPE_LOGIN_STATUS, this[_statusHandler]);
		
		
		if(this.api.loggedIn && this.api.isPhysician)
			this.getDoctorKneeProtocol();
	}
	disconnectedCallback() {
		super.disconnectedCallback();

		this.api.off(null, this[_statusHandler]);
		this.api = null;
	}
	updated() {
		if(this.doctorKneeProtocol) {
			this.shadowRoot.querySelector('select[name="targetFlexion"]').value = this.doctorKneeProtocol.targetFlexion;
			this.shadowRoot.querySelector('select[name="targetExtension"]').value = this.doctorKneeProtocol.targetExtension;
			this.shadowRoot.querySelector('select[name="sessionsPerDay"]').value = this.doctorKneeProtocol.sessionsPerDay;
			this.shadowRoot.querySelector('select[name="sessionDuration"]').value = this.doctorKneeProtocol.sessionDuration;
		}
	}
	render() {
		return html`
			<fieldset>
				<legend>CPM Protocol</legend>
				<p class="info">
					<i class="icon fas fa-info-circle"></i>
					Select the Knee CPM Protocol you would like your patients to follow.
				</p>
				<div class="options">
					<div>
						<div>Target Flexion</div>
						<select name="targetFlexion" @change="${e => this.handleTargetFlexion(e)}">
							<option value="120">120</option>
							<option value="125">125</option>
						</select>
					</div>
					<div>
						<div>Target Extension</div>
						<select name="targetExtension" @change="${e => this.handleTargetExtension(e)}">
							<option value="0">0</option>
						</select>
					</div>
					<div>
						<div>Sessions Per Day</div>
						<select name="sessionsPerDay" @change="${e => this.handleSessionsPerDay(e)}">
							<option value="1">1</option>
							<option value="2">2</option>
							<option value="3">3</option>
						</select>
					</div>
					<div>
						<div>Session Duration</div>
						<select name="sessionDuration" @change="${e => this.handleSessionDuration(e)}">
							<option value="30">30 Minutes</option>
							<option value="60">60 Minutes</option>
							<option value="90">90 Minutes</option>
						</select>
					</div>
				</div>
			</fieldset>
		`;
	}
}

customElements.define('pp-knee-protocol', SurveyQuestions);